<template>
    <div
        class="hts-dropdown"
        :class="htsDropdownClass">
        <hts-popover
            ref="popover"
            :trigger="trigger"
            :placement="placement"
            :offset="offset"
            :arrow="false">
            <template #reference>
                <slot name="reference">
                    <button class="btn btn-outline">
                        {{ $t('labels.select') }}
                    </button>
                </slot>
            </template>
            <ul
                v-if="!multipleLevel"
                tabindex="0"
                class="dropdown-content menu">
                <template v-if="options.length">
                    <li
                        v-for="(item, index) in options"
                        :key="index"
                        class="rounded"
                        @click="handleAction(item || '')">
                        <slot :item="item">
                            <div
                                class="flex m-1 p-2 outline-0 !rounded"
                                :class="item.class">
                                <component
                                    :is="item[iconKey]"
                                    v-if="item[iconKey]"
                                    class="icon w-4 h-4" />
                                <span class="p-0 leading-5">
                                    {{ item[labelKey] || '...' }}
                                </span>
                            </div>
                        </slot>
                    </li>
                </template>
                <template v-else>
                    <li>
                        <span>
                            No data
                        </span>
                    </li>
                </template>
            </ul>

            <items
                v-else
                :options="options"
                :value-key="valueKey"
                :icon-key="iconKey"
                :handle-action="handleAction" />
        </hts-popover>
    </div>
</template>

<script lang="ts" setup>
import items from './item.vue'

const props = defineProps({
  /***
   * Required props
   */
  options: {
    type: Array,
    required: true
  },
  placement: {
    type: String,
    default: 'left-start'
  },
  offset: {
    type: Array,
    default: () => [0, 0]
  },
  position: String,
  hover: {
    type: Boolean,
    default: true
  },
  /***
   * Dropdown attributes
   */
  trigger: {
    type: String,
    default: 'mouseenter focus' // 'hover', 'click'
  },
  label: {
    type: String,
    default: 'Options'
  },
  labelOnlyIcon: {
    type: Boolean,
    default: false
  },
  icon: {
    type: String,
    default: 'hi-chevron-down-icon'
  },
  color: {
    type: String,
    default: 'info' // 'active', 'info', 'success', 'error', 'warning', 'disable', 'default', 'main', 'theme'
  },
  size: {
    type: String,
    default: 'small' // 'mini', 'small', 'default', 'large', 'x-large'
  },
  type: {
    type: String,
    default: 'text' // button, text, link
  },
  disabled: {
    type: Boolean,
    default: false
  },
  showTimeout: {
    type: Number,
    default: 50
  },
  hideTimeout: {
    type: Number,
    default: 100
  },
  /***
   * Properties dropdown options
   */
  valueKey: {
    type: String,
    default: 'value'
  },
  labelKey: {
    type: String,
    default: 'label'
  },
  iconKey: {
    type: String,
    default: 'icon'
  },
  propOptionKey: {
    type: String,
    default: 'key'
  },
  propOptionLabel: {
    type: String,
    default: 'label'
  },
  propOptionIcon: {
    type: String,
    default: 'icon'
  },
  multipleLevel: {
    type: Boolean,
    default: false
  }
})
const emits = defineEmits(['click'])
const popover: any = ref(null)
/***
 * Computed
 */
const sizeClass = computed(() => {
  if ($lodash.isNil(props.size)) {
    return 'size-default'
  }
  return `size-${props.size}`
})

const colorClass = computed(() => {
  if ($lodash.isNil(props.color) || typeIsButton.value) {
    return 'color-default'
  }

  return `color-${props.color}`
})

const backgroundClass = computed(() => {
  if (typeIsButton.value) {
    return `bg-${props.color}`
  }
  return ''
})

const typeIsButton = computed(() => {
  return props.type === 'button'
})

const htsDropdownClass = computed(() => {
  return {
    [sizeClass.value]: true,
    [colorClass.value]: true,
    [backgroundClass.value]: true
  }
})

const handleAction = (item: any) => {
  popover.value && popover.value.hide()
  emits('click', item)
}
</script>
